import React from 'react';

type DiffLine = {
    type: 'none' | 'del' | 'add';
    indent: number;
    content: string;
};

function buildLines(oldObj: any, newObj: any, indent = 0): DiffLine[] {
    const lines: DiffLine[] = [];

    const isRoot = indent === 0;
    if (isRoot) {
        lines.push({ type: 'none', indent: 0, content: '{' });
    }

    const oldKeys = Object.keys(oldObj ?? {});
    const newKeys = Object.keys(newObj ?? {});
    const allKeys = Array.from(new Set([...oldKeys, ...newKeys])).sort();
    const effectiveIndent = isRoot ? 1 : indent;

    allKeys.forEach((key, keyIndex) => {
        const isLastKey = keyIndex === allKeys.length - 1;
        const inOld = key in (oldObj ?? {});
        const inNew = key in (newObj ?? {});
        const oldVal = oldObj?.[key];
        const newVal = newObj?.[key];

        const isOldComplex = oldVal !== null && typeof oldVal === 'object';
        const isNewComplex = newVal !== null && typeof newVal === 'object';

        if (inOld && inNew && JSON.stringify(oldVal) === JSON.stringify(newVal)) {
            // Unchanged
            if (isOldComplex) {
                lines.push({ type: 'none', indent: effectiveIndent, content: `${key}: {` });
                lines.push(...buildLines(oldVal, newVal, effectiveIndent + 1));
                lines.push({ type: 'none', indent: effectiveIndent, content: `}${isLastKey ? '' : ','}` });
            } else {
                lines.push({ type: 'none', indent: effectiveIndent, content: `${key}: ${JSON.stringify(oldVal)}${isLastKey ? '' : ','}` });
            }
        } else if (inOld && inNew && isOldComplex && isNewComplex) {
            // Both complex, recurse
            lines.push({ type: 'none', indent: effectiveIndent, content: `${key}: {` });
            lines.push(...buildLines(oldVal, newVal, effectiveIndent + 1));
            lines.push({ type: 'none', indent: effectiveIndent, content: `}${isLastKey ? '' : ','}` });
        } else if (inOld && inNew) {
            // Value changed (primitive)
            lines.push({ type: 'del', indent: effectiveIndent, content: `${key}: ${JSON.stringify(oldVal)}${isLastKey ? '' : ','}` });
            lines.push({ type: 'add', indent: effectiveIndent, content: `${key}: ${JSON.stringify(newVal)}${isLastKey ? '' : ','}` });
        } else if (inOld && !inNew) {
            // Deleted
            if (isOldComplex) {
                lines.push({ type: 'del', indent: effectiveIndent, content: `${key}: {` });
                lines.push(...buildLines(oldVal, {}, effectiveIndent + 1).map(l => ({ ...l, type: 'del' as const })));
                lines.push({ type: 'del', indent: effectiveIndent, content: `}${isLastKey ? '' : ','}` });
            } else {
                lines.push({ type: 'del', indent: effectiveIndent, content: `${key}: ${JSON.stringify(oldVal)}${isLastKey ? '' : ','}` });
            }
        } else {
            // Added
            if (isNewComplex) {
                lines.push({ type: 'add', indent: effectiveIndent, content: `${key}: {` });
                lines.push(...buildLines({}, newVal, effectiveIndent + 1).map(l => ({ ...l, type: 'add' as const })));
                lines.push({ type: 'add', indent: effectiveIndent, content: `}${isLastKey ? '' : ','}` });
            } else {
                lines.push({ type: 'add', indent: effectiveIndent, content: `${key}: ${JSON.stringify(newVal)}${isLastKey ? '' : ','}` });
            }
        }
    });

    if (isRoot) {
        lines.push({ type: 'none', indent: 0, content: '}' });
    }

    return lines;
}

interface Props {
    oldData: any;
    newData: any;
}

const JsonDiffView: React.FC<Props> = ({ oldData, newData }) => {
    const lines = buildLines(oldData, newData);

    if (lines.length === 0) {
        return <div className="json-diff-empty">No differences found.</div>;
    }

    return (
        <div className="json-diff-view">
            <pre className="json-diff-pre">
                {lines.map((line, i) => (
                    <div
                        key={i}
                        className={`json-diff-line json-diff-line--${line.type}`}
                        style={{ paddingLeft: `${line.indent * 20 + 16}px` }}
                    >
                        <span className="json-diff-marker">
                            {line.type === 'del' ? '- ' : line.type === 'add' ? '+ ' : '  '}
                        </span>
                        <span className="json-diff-content">{line.content}</span>
                    </div>
                ))}
            </pre>
        </div>
    );
};

export default JsonDiffView;
